import type { InputHTMLAttributes, ReactNode } from 'react';

import { EyeIcon, EyeInvisibleIcon } from './AuthIcons';

interface AuthFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'> {
  id: string;
  label: string;
  trailing?: ReactNode;
  /** Decorative icons use a non-interactive wrapper; buttons render directly. */
  trailingInteractive?: boolean;
  invalid?: boolean;
  message?: ReactNode;
  inputClassName?: string;
}

/** Glassmorphism notch-label field (reference login form). */
export function AuthField({
  label,
  trailing,
  trailingInteractive,
  invalid,
  message,
  inputClassName,
  id,
  ...inputProps
}: AuthFieldProps) {
  return (
    <div className="auth-field">
      <label className="auth-field-label" htmlFor={id}>
        {label}
      </label>
      <div className="auth-field-control">
        <input
          id={id}
          className={['auth-field-input', invalid ? 'auth-field-input--invalid' : '', inputClassName]
            .filter(Boolean)
            .join(' ')}
          aria-invalid={invalid || undefined}
          {...inputProps}
        />
        {trailing
          ? trailingInteractive
            ? trailing
            : <div className="auth-field-trailing">{trailing}</div>
          : null}
      </div>
      {message}
    </div>
  );
}

interface AuthPasswordFieldProps extends Omit<AuthFieldProps, 'type' | 'trailing'> {
  showPassword: boolean;
  onToggleShow: () => void;
  showLabel: string;
  hideLabel: string;
}

export function AuthPasswordField({
  showPassword,
  onToggleShow,
  showLabel,
  hideLabel,
  ...fieldProps
}: AuthPasswordFieldProps) {
  return (
    <AuthField
      {...fieldProps}
      type={showPassword ? 'text' : 'password'}
      trailingInteractive
      trailing={
        <button
          type="button"
          className="auth-field-trailing-btn"
          onClick={onToggleShow}
          aria-label={showPassword ? hideLabel : showLabel}
        >
          {showPassword ? <EyeInvisibleIcon /> : <EyeIcon />}
        </button>
      }
    />
  );
}

interface AuthFieldSlotProps {
  id: string;
  label: string;
  children: ReactNode;
  className?: string;
  message?: ReactNode;
}

/** Notch label wrapper for composite controls (phone row, OTP, etc.). */
export function AuthFieldSlot({ id, label, children, className, message }: AuthFieldSlotProps) {
  return (
    <div className={['auth-field', className].filter(Boolean).join(' ')} role="group" aria-labelledby={id}>
      <span className="auth-field-label" id={id}>
        {label}
      </span>
      <div className="auth-field-control auth-field-control--slot">{children}</div>
      {message}
    </div>
  );
}
